import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { APP_IMG } from 'assets';
import { TOTAL_TIME_EXPIRED_QRCODE } from 'constants/global';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { uploadAPIs } from 'services/upload';
import { useAppDispatch } from 'store/hook';
import { setGlobalLoading } from 'store/reducers/global';
import { downloadFile, formatDate, removeLocalStorage } from 'utils';
import {
  BoxContent,
  LoadingOverlay,
  StyledImage,
  StyledText,
  StyledTitle,
} from './styles';

interface PaymentPayNowProps {
  valueQrCode: string;
  qrCodeKey?: string;
  retryPaymentCallback: () => void;
}

type TimeUnit = {
  minutes: number;
  remainingSeconds: number;
};

export const PaymentPayNow = ({
  valueQrCode,
  qrCodeKey,
  retryPaymentCallback,
}: PaymentPayNowProps) => {
  const dispatch = useAppDispatch();
  const [countDown, setCountDown] = useState<number>(TOTAL_TIME_EXPIRED_QRCODE);
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

  // Preload image
  useEffect(() => {
    if (!valueQrCode) return;

    const img = new Image();
    img.src = valueQrCode;
    img.onload = () => {
      setIsImageLoaded(true);
    };
  }, [valueQrCode]);

  useEffect(() => {
    setCountDown(TOTAL_TIME_EXPIRED_QRCODE);
    const interval = setInterval(() => {
      setCountDown((pre) => {
        if (pre <= 1) {
          clearInterval(interval);
          return 0;
        }
        return pre - 1;
      });
    }, 1_000);
    return () => clearInterval(interval);
  }, [valueQrCode]);

  const formatCountdown = (seconds: number): string => {
    const EXPIRED_MESSAGE = '00:00';
    if (seconds <= 0) {
      removeLocalStorage(qrCodeKey);
      return EXPIRED_MESSAGE;
    }

    // Handles the time unit calculations
    const getTimeUnits = (seconds: number): TimeUnit => ({
      minutes: Math.floor(seconds / 60),
      remainingSeconds: seconds % 60,
    });

    // Handles the formatting of numbers
    const padWithZero = (num: number): string =>
      num.toString().padStart(2, '0');

    return `${padWithZero(getTimeUnits(seconds).minutes)}:${padWithZero(
      getTimeUnits(seconds).remainingSeconds,
    )}`;
  };

  const handleDownloadImage = async () => {
    dispatch(setGlobalLoading(true));
    await uploadAPIs
      .uploadByUrl({ url: valueQrCode })
      .then(({ data }) => {
        downloadFile({ imageFileName: 'qrCode.png', imageSrc: data.data });
      })
      .catch((e) => {
        console.log(e);
        dispatch(setGlobalLoading(false));
      })
      .finally(() => {
        dispatch(setGlobalLoading(false));
      });
  };

  if (!isImageLoaded) {
    return (
      <LoadingOverlay>
        <CircularProgress />
      </LoadingOverlay>
    );
  }

  return (
    <React.Fragment>
      <Stack spacing={4} mt={5}>
        <div>
          <BoxContent spacing={2} padding={2}>
            {countDown > 0 ? (
              <>
                <Stack
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  <Stack spacing={1}>
                    <StyledTitle>Pending Payment</StyledTitle>
                    <StyledText>
                      Make Payment by&nbsp;
                      {formatDate(moment().add(10, 'minutes'), {
                        format: 'hh:mm A',
                      })}
                    </StyledText>
                  </Stack>
                  <Stack
                    bgcolor={'primary.main'}
                    padding={0.75}
                    borderRadius={1}
                  >
                    <Typography
                      variant="Components_Button"
                      color={'white.light'}
                    >
                      {formatCountdown(countDown)}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack alignItems={'center'}>
                  <StyledImage
                    id="qrCodeImg"
                    src={valueQrCode}
                    alt="qr_code"
                    isQRcode
                  />
                  <StyledText>Save QR Code to your photo library</StyledText>
                </Stack>
                <Button
                  variant="contained"
                  sx={{ height: 32 }}
                  onClick={handleDownloadImage}
                >
                  Save QR Code
                </Button>
              </>
            ) : (
              <Stack spacing={4} height={220} justifyContent={'center'}>
                <Stack>
                  <StyledText textAlign={'center'}>
                    The QR code has expired!
                  </StyledText>
                  <StyledText textAlign={'center'}>
                    Click Try Again button to continue payment.
                  </StyledText>
                </Stack>
                <Button
                  variant="contained"
                  sx={{ height: 32 }}
                  onClick={retryPaymentCallback}
                >
                  Try Again
                </Button>
              </Stack>
            )}
          </BoxContent>
        </div>
        <div>
          <BoxContent spacing={2} padding={2}>
            <StyledTitle>How to Pay</StyledTitle>
            <StyledText>
              Login to your banking app with PayNow and look for the scan and
              pay feature
            </StyledText>
            <StyledImage src={APP_IMG.scanQRcode} alt="scan_qr_code" />
            <StyledText>
              Upload the saved QR Code from your photo library and make payment.
            </StyledText>
          </BoxContent>
        </div>
      </Stack>
    </React.Fragment>
  );
};

export default PaymentPayNow;
